// The edges between two assets: an alternative cut, a source, a recommendation.

import { api } from '../client'
import type { components } from '../schema'

export type AssetRelationshipEntry = components['schemas']['AssetRelationshipEntry']
export type CreateAssetRelationshipRequest = components['schemas']['CreateAssetRelationshipRequest']
export type UpdateAssetRelationshipRequest = components['schemas']['UpdateAssetRelationshipRequest']

function relationshipsPath(assetId: string): string {
  return `/assets/${encodeURIComponent(assetId)}/relationships`
}

/**
 * Every live edge touching an asset, from both ends, by kind and then by position. A directed
 * edge read from its far end carries `is_reverse: true`: "B derived_from A" read on A.
 */
export function listAssetRelationships(assetId: string, signal?: AbortSignal): Promise<AssetRelationshipEntry[]> {
  return api.get<AssetRelationshipEntry[]>(relationshipsPath(assetId), undefined, signal)
}

/**
 * Join an asset to another, from it to `to_asset_id`. One row: a symmetric kind's twin is a second
 * call. 409 when that edge, or the reverse of a directed one, already stands.
 */
export function createAssetRelationship(
  assetId: string,
  request: CreateAssetRelationshipRequest,
): Promise<AssetRelationshipEntry> {
  return api.post<AssetRelationshipEntry>(relationshipsPath(assetId), request)
}

/**
 * Publish an edge, withdraw it, or change its description. On a symmetric entry read in reverse
 * (`is_reverse`), this writes the asset's own row and answers it under a new uid; a directed kind
 * read in reverse is a 400, and so is publishing a directed kind.
 */
export function updateAssetRelationship(
  assetId: string,
  relationshipId: string,
  request: UpdateAssetRelationshipRequest,
): Promise<AssetRelationshipEntry> {
  return api.patch<AssetRelationshipEntry>(
    `${relationshipsPath(assetId)}/${encodeURIComponent(relationshipId)}`,
    request,
  )
}

/** Unlink two assets. */
export function deleteAssetRelationship(assetId: string, relationshipId: string): Promise<void> {
  return api.del<void>(`${relationshipsPath(assetId)}/${encodeURIComponent(relationshipId)}`)
}
